// Hosted-mode identity panel for the handoff experience.
//
// This is the focused hosted-mode UI gating this lane owns. It is a
// REPLACEMENT surface, not a filter: in hosted mode the handoff experience
// shows GINI account identity, tenant, plan, and a platform-managed
// execution notice — and there is no BYOK field, provider selector, model
// selector, provider OAuth control, custom upstream URL, or local provider
// configuration anywhere in the handoff component tree to hide in the first
// place. Absence by construction is what the "no BYOK fallback" test
// asserts against the rendered DOM.
//
// Scope: deliberately limited to the handoff surface. The full Settings
// dialog is NOT redesigned here — that is a separate migration
// (docs/gini-platform/hosted-mode-ui-migration.md), and server-side
// rejection remains the authoritative boundary either way. This panel is
// presentation; it is not a security control.

import type { GiniHandoffCapabilities } from '@open-design/contracts/gini-platform';

import { useT } from '../../i18n';
import type { AuthMeResponse } from '../../providers/auth';
import { CapabilityNotice } from './CapabilityNotice';
import styles from './HandoffLanding.module.css';

interface Props {
  auth: AuthMeResponse | null;
  capabilities: GiniHandoffCapabilities;
}

export function HostedAccountPanel({ auth, capabilities }: Props) {
  const t = useT();
  const hosted = auth?.mode === 'server';

  return (
    <section className={styles.card} aria-labelledby="gini-handoff-account" data-testid="gini-handoff-account">
      <h2 className={styles.cardTitle} id="gini-handoff-account">
        {t('giniHandoff.accountHeading')}
      </h2>

      <div className={styles.metaGrid}>
        {auth?.email ? (
          <div>
            <span className={styles.metaLabel}>{t('giniHandoff.accountEmail')}</span>
            <span className={styles.metaValue}>{auth.email}</span>
          </div>
        ) : null}
        {auth?.tenantId ? (
          <div>
            <span className={styles.metaLabel}>{t('giniHandoff.accountTenant')}</span>
            <span className={styles.metaValue} data-testid="gini-handoff-tenant">
              {auth.tenantId}
            </span>
          </div>
        ) : null}
        {auth?.plan ? (
          <div>
            <span className={styles.metaLabel}>{t('giniHandoff.accountPlan')}</span>
            <span className={styles.metaValue}>{auth.plan}</span>
          </div>
        ) : null}
      </div>

      {hosted ? (
        <p className={styles.empty} data-testid="gini-handoff-platform-managed">
          {t('giniHandoff.platformManaged')}
        </p>
      ) : null}

      <CapabilityNotice capability={capabilities.productionObjectStorage} testId="gini-handoff-storage-notice" />
    </section>
  );
}
